import { useAws } from "@/context/AwsContext";
import {
  Globe2, Network, ShieldCheck, Scale, Server, TrendingUp, Database, MemoryStick,
  ChevronDown, Router, DoorOpen, Waypoints, Boxes,
} from "lucide-react";

// A custom, dependency-free top-to-bottom architecture map of the HMG
// 3-tier production environment. Every node is clickable and jumps to the
// matching service console view.
function Node({ icon: Icon, title, sub, tone = "slate", onClick, testid, wide }) {
  const tones = {
    orange: "border-[#ff9900]/60 hover:border-[#ff9900] bg-[#ff9900]/5",
    blue: "border-[#4da3ff]/50 hover:border-[#4da3ff] bg-[#4da3ff]/5",
    emerald: "border-emerald-500/50 hover:border-emerald-400 bg-emerald-500/5",
    rose: "border-rose-500/50 hover:border-rose-400 bg-rose-500/5",
    slate: "border-[#414750] hover:border-slate-400 bg-[#161e2d]",
  };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`group relative rounded-lg border ${tones[tone]} px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/40 ${wide ? "min-w-[240px]" : "min-w-[150px]"}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-[#ff9900] shrink-0" />
        <span className="text-sm font-semibold text-white">{title}</span>
      </div>
      {sub && <div className="text-[11px] text-slate-400 mt-1 leading-relaxed">{sub}</div>}
    </button>
  );
}

function Connector({ label }) {
  return (
    <div className="flex flex-col items-center py-1" aria-hidden>
      <div className="w-px h-4 bg-gradient-to-b from-[#414750] to-[#ff9900]/60" />
      <ChevronDown className="h-4 w-4 text-[#ff9900]/70 -my-1" />
      {label && <div className="text-[10px] text-slate-500 mt-0.5">{label}</div>}
    </div>
  );
}

function Layer({ label, children }) {
  return (
    <div className="flex flex-col items-center">
      {label && <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">{label}</div>}
      <div className="flex flex-wrap items-stretch justify-center gap-3">{children}</div>
    </div>
  );
}

export default function AwsTopology({ goView }) {
  const { byType, region } = useAws();
  const b = (t) => byType(t);
  const webAsg = b("asg").find((a) => /WEB/i.test(a.name));
  const apiAsg = b("asg").find((a) => /API/i.test(a.name));
  const alb = b("elb").find((e) => e.lb_type === "application");
  const nlb = b("elb").find((e) => e.lb_type === "network");
  const cf = b("cloudfront")[0];
  const waf = b("waf_acl")[0];
  const zone = b("hosted_zone")[0];
  const primary = b("db_instance").find((d) => /Primary|writer/i.test(d.role || ""));
  const replica = b("db_instance").find((d) => /replica/i.test(d.role || ""));
  const redis = b("cache_cluster")[0];
  const webCount = (webAsg?.instances || []).length;
  const apiCount = (apiAsg?.instances || []).length;
  const bastion = b("instance").find((i) => /BASTION/i.test(i.name));
  const nats = b("nat_gateway");

  return (
    <div className="p-6" data-testid="aws-topology-view">
      <div className="flex items-center gap-2 mb-1">
        <Waypoints className="h-5 w-5 text-[#ff9900]" />
        <h1 className="text-xl font-semibold text-white">Resource Topology · HMG Production Architecture</h1>
      </div>
      <p className="text-slate-400 text-xs mb-6">3-tier, multi-AZ production environment in {region}. Click any component to open its console view.</p>

      <div className="rounded-xl border border-[#414750] bg-[#0c1526] p-6 overflow-x-auto">
        <div className="flex flex-col items-center gap-1 min-w-[640px]">
          {/* Edge / DNS */}
          <Layer label="Internet edge">
            <Node icon={Globe2} title="Route 53" sub={zone?.domain || "hmgportal.example"} tone="blue" testid="topo-route53" onClick={() => goView("route53")} />
          </Layer>
          <Connector label="DNS resolves to" />
          <Layer>
            <Node icon={ShieldCheck} title="AWS WAF" sub={`${(waf?.rules || []).length} rules · ${(waf?.blocked_requests_24h || 0).toLocaleString()} blocked/24h`} tone="rose" testid="topo-waf" onClick={() => goView("waf")} />
            <Node icon={Network} title="CloudFront" sub={cf?.domain_name || "CDN distribution"} tone="orange" testid="topo-cloudfront" onClick={() => goView("cloudfront")} />
          </Layer>
          <Connector label="HTTPS" />

          {/* VPC boundary */}
          <div className="w-full max-w-3xl rounded-xl border-2 border-dashed border-[#ff9900]/30 p-5 mt-1">
            <div className="text-[11px] text-[#ff9900] font-semibold mb-4 flex items-center gap-1.5"><Boxes className="h-3.5 w-3.5" /> HMG-PROD-VPC · 10.20.0.0/16 · 2 Availability Zones</div>
            <div className="flex flex-col items-center gap-1">
              <Layer label="Public subnets">
                <Node icon={DoorOpen} title="Internet Gateway" sub="Ingress / egress" testid="topo-igw" onClick={() => goView("vpc")} />
                <Node icon={Scale} title={alb?.name || "ALB"} sub="Application LB · internet-facing" tone="orange" testid="topo-alb" onClick={() => goView("load-balancers")} />
                <Node icon={Router} title={`${nats.length} NAT Gateways`} sub="Private egress" testid="topo-nat" onClick={() => goView("vpc")} />
                {bastion && <Node icon={Server} title="Bastion" sub={bastion.private_ip} tone="blue" testid="topo-bastion" onClick={() => goView("ec2")} />}
              </Layer>
              <Connector />
              <Layer label="Web tier (private)">
                <Node icon={TrendingUp} title={webAsg?.name || "Web ASG"} tone="emerald" wide
                  sub={`Auto Scaling ${webCount}/${webAsg?.desired ?? 2} · min ${webAsg?.min ?? 2} / max ${webAsg?.max ?? 6}`}
                  testid="topo-web-asg" onClick={() => goView("auto-scaling")} />
                <Node icon={Server} title={`${webCount}× Web EC2`} sub="t3.medium · nginx" tone="blue" testid="topo-web-ec2" onClick={() => goView("ec2")} />
              </Layer>
              <Connector label="internal traffic" />
              <Layer label="Internal load balancing">
                <Node icon={Scale} title={nlb?.name || "API NLB"} sub="Network LB · internal" tone="orange" testid="topo-nlb" onClick={() => goView("load-balancers")} />
              </Layer>
              <Connector />
              <Layer label="API tier (private)">
                <Node icon={TrendingUp} title={apiAsg?.name || "API ASG"} tone="emerald" wide
                  sub={`Auto Scaling ${apiCount}/${apiAsg?.desired ?? 2} · min ${apiAsg?.min ?? 2} / max ${apiAsg?.max ?? 6}`}
                  testid="topo-api-asg" onClick={() => goView("auto-scaling")} />
                <Node icon={Server} title={`${apiCount}× API EC2`} sub="t3.medium · app :8080" tone="blue" testid="topo-api-ec2" onClick={() => goView("ec2")} />
              </Layer>
              <Connector label="data access" />
              <Layer label="Data tier (isolated)">
                <Node icon={Database} title={primary?.id || "RDS Primary"} sub={`MySQL · ${primary?.multi_az ? "Multi-AZ writer" : "writer"}`} tone="emerald" testid="topo-rds-primary" onClick={() => goView("rds")} />
                <Node icon={Database} title={replica?.id || "RDS Replica"} sub={`Read replica · lag ${replica?.replication_lag_ms ?? 0}ms`} tone="blue" testid="topo-rds-replica" onClick={() => goView("rds")} />
                <Node icon={MemoryStick} title="ElastiCache" sub={`Redis · ${redis?.node_count ?? 2} nodes`} tone="rose" testid="topo-redis" onClick={() => goView("cache")} />
              </Layer>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-3 text-[11px] text-slate-400">
        {[["orange", "Load balancing / CDN"], ["emerald", "Auto-scaled / managed"], ["blue", "Compute / replica"], ["rose", "Security / cache"]].map(([t, l]) => (
          <span key={t} className="inline-flex items-center gap-1.5">
            <span className={`h-2.5 w-2.5 rounded-sm ${t === "orange" ? "bg-[#ff9900]" : t === "emerald" ? "bg-emerald-500" : t === "blue" ? "bg-[#4da3ff]" : "bg-rose-500"}`} />{l}</span>
        ))}
      </div>
    </div>
  );
}
